import AbstractConnector, { RequestOptions, SaveOptions } from './AbstractConnector';
import _get                 from 'lodash.get';
import PagedCollection      from './PagedCollection';
import objectCleanUndefined from '../utils/objectCleanUndefined';

export default abstract class AbstractModel {
	protected abstract _path: string;
	protected abstract _connector: AbstractConnector;
	protected _translations?: RequestTranslations;
	protected _requestReportStore?: RequestTracer;

	protected _attributes: ModelAttributes = {};

	abstract _tsFilters: Record<string, unknown>;

	constructor(
		attributes: ModelAttributes = {},
		options?: ModelOptions,
	) {
		this._attributes = attributes;
		this._requestReportStore = options?.requestReportStore;
	}

	get path() {
		return this.connector.connectorPath + this._path;
	}

	get connector() {
		return this._connector;
	}

	async fetch(options?: RequestOptions) {
		const response = await this.connector.fetch(this.fullPath, {
			requestReportStore: this._requestReportStore,
			...options,
			headers: objectCleanUndefined({
				'X-LOCALE': this._translations,
				...options?.headers,
			}),
		});

		const attributes = response.data as ModelAttributes;

		this.setAttributes(attributes);

		return { model: this, response };
	}

	async fetchWithFilters(filters: Partial<this['_tsFilters']>) {
		const collection = new PagedCollection(this.constructor as ModelConstructor, {
			requestReportStore: this._requestReportStore,
		});

		Object.entries(filters).forEach(([key, value]) => {
			collection.setFilter(key, value);
		});

		const { response } = await collection
			.setItemsPerPage(1)
			.setTranslations(this._translations)
			.list();

		if (collection.first) {
			this.setAttributes(collection.first.attributes);
		}

		return { model: this, response };
	}

	async save(attributes = this.attributes, saveOptions?: SaveOptions) {
		const response = await this.connector.save(
			this.fullPath,
			attributes,
			{
				method: this.id ? 'PATCH' : 'POST',
				requestReportStore: this._requestReportStore,
				silentError: saveOptions?.silentError,
			},
			saveOptions,
		);

		this.setAttributes(response.data as ModelAttributes);

		return { model: this, response };
	}

	async patch(attributes: ModelAttributes, saveOptions?: SaveOptions) {
		return this.save(attributes, saveOptions);
	}

	async destroy() {
		const response = await this.connector.destroy(this.fullPath);
		this.setAttributes({});
		return { response };
	}

	get fullPath() {
		// Remplacement des paramètres
		let result = this.path.replace(/\{\?([^}]+)}/g, (_, key) => {
			return key in this._attributes && this._attributes[key] != null ? String(this._attributes[key]) : '';
		});

		// Suppression du slash final sauf si c'est la racine "/"
		if (result.length > 1 && result.endsWith('/')) {
			result = result.slice(0, -1);
		}

		return result;
	}

	setAttributes(attributes: ModelAttributes) {
		this._attributes = attributes;
		return this;
	}

	get attributes() {
		return this._attributes;
	}

	get id() {
		return this.attributes.id as id;
	}

	protected get<R, D>(name: string, defaultValue?: D): R | D {
		return _get(this.attributes, name, defaultValue) as R | D;
	}

	setTranslations(translations?: RequestTranslations) {
		this._translations = translations;
		return this;
	}

	async fetchResolvable<
		// --- Niveau 1 ---
		N1 extends ModelPropKeys<this> & keyof InstanceOf<this>,
		Obj1 extends InstanceOf<this>[N1],
		// --- Niveau 2 ---
		N2 extends ModelPropKeys<Obj1> & keyof Obj1,
		Obj2 extends Obj1[N2],
		// --- Niveau 3 ---
		N3 extends ModelPropKeys<Obj2> & keyof Obj2,
		Obj3 extends Obj2[N3],
		// --- Niveau 4 ---
		N4 extends ModelPropKeys<Obj3> & keyof Obj3,
		Obj4 extends Obj3[N4],
		// --- Niveau 5 ---
		N5 extends keyof Obj4
	>(
		name1: N1,
		name2?: N2,
		name3?: N3,
		name4?: N4,
		name5?: N5,
	) {
		const collection = new PagedCollection(this.constructor as any, { requestReportStore: this._requestReportStore });
		collection.setModels([this]);
		return collection.fetchResolvable(name1 as any, name2 as any, name3 as any, name4 as any, name5 as any);
	}
}
